// Import the functions you need from the SDKs you need
import { initializeApp } from 'firebase/app'
import { getAnalytics, isSupported as analyticsIsSupported } from 'firebase/analytics'
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  connectAuthEmulator
} from 'firebase/auth'
import {
  initializeFirestore,
  connectFirestoreEmulator,
  setLogLevel,
  collection,
  getDocs,
  setDoc,
  doc,
  enableNetwork,
  disableNetwork,
  persistentLocalCache,
  persistentMultipleTabManager
} from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions'

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: 'AIzaSyD43mptHgOVpOGvixWt1n0D6lRlfQIt2ak',
  authDomain: 'dnd-playground-92d3c.firebaseapp.com',
  projectId: 'dnd-playground-92d3c',
  databaseURL: 'https://dnd-playground-92d3c.firebaseio.com',
  storageBucket: 'dnd-playground-92d3c.firebasestorage.app',
  messagingSenderId: '119288520464',
  appId: '1:119288520464:web:363e1dc2790ce17372cf95',
  measurementId: 'G-723GQ1SLE6'
}

// Only show warnings and errors, even in development mode
setLogLevel('warning')

const env = typeof import.meta !== 'undefined' && import.meta?.env ? import.meta.env : {}

function parseBooleanEnv(value, fallback = false) {
  if (value == null) return fallback
  const normalized = String(value).trim().toLowerCase()
  if (!normalized) return fallback
  return ['1', 'true', 'yes', 'on'].includes(normalized)
}

function parsePositiveIntEnv(value, fallback) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback
  return Math.floor(parsed)
}

function normalizeEmulatorHost(value) {
  const host = String(value || '').trim()
  if (!host) return 'localhost'
  if (/^(127\.0\.0\.1|0\.0\.0\.0|::1)$/i.test(host)) return 'localhost'
  return host
}

const allowProdEmulators = parseBooleanEnv(env.VITE_FIREBASE_ALLOW_PROD_EMULATORS, false)
const isProdBuild = Boolean(env.PROD)
const requestedEmulators = parseBooleanEnv(env.VITE_FIREBASE_USE_EMULATORS, false)
const useFirebaseEmulators = requestedEmulators && (!isProdBuild || allowProdEmulators)
const firebaseEmulatorHost = normalizeEmulatorHost(env.VITE_FIREBASE_EMULATOR_HOST || 'localhost')
const firebaseAuthEmulatorPort = parsePositiveIntEnv(env.VITE_FIREBASE_AUTH_PORT, 9099)
const firebaseFirestoreEmulatorPort = parsePositiveIntEnv(env.VITE_FIREBASE_FIRESTORE_PORT, 8080)
const firebaseFunctionsEmulatorPort = parsePositiveIntEnv(env.VITE_FIREBASE_FUNCTIONS_PORT, 5001)

if (requestedEmulators && isProdBuild && !allowProdEmulators && typeof console !== 'undefined') {
  console.warn(
    '[firebase] Ignoring emulator settings in production build. Set VITE_FIREBASE_ALLOW_PROD_EMULATORS=true to override.'
  )
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)
const auth = getAuth(app)
const functions = getFunctions(app)

if (useFirebaseEmulators && typeof window !== 'undefined') {
  try {
    connectAuthEmulator(auth, `http://${firebaseEmulatorHost}:${firebaseAuthEmulatorPort}`, {
      disableWarnings: true
    })
  } catch (error) {
    if (import.meta?.env?.DEV) {
      console.warn('[firebase] connectAuthEmulator skipped:', error?.message || error)
    }
  }

  try {
    connectFunctionsEmulator(functions, firebaseEmulatorHost, firebaseFunctionsEmulatorPort)
  } catch (error) {
    if (import.meta?.env?.DEV) {
      console.warn('[firebase] connectFunctionsEmulator skipped:', error?.message || error)
    }
  }
}

// Set persistence to local by default
setPersistence(auth, browserLocalPersistence)

// Initialize Firestore with persistent cache
const db = initializeFirestore(
  app,
  {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager()
    })
  },
  'dnd-playground'
)

if (useFirebaseEmulators && typeof window !== 'undefined') {
  try {
    connectFirestoreEmulator(db, firebaseEmulatorHost, firebaseFirestoreEmulatorPort)
  } catch (error) {
    if (import.meta?.env?.DEV) {
      console.warn('[firebase] connectFirestoreEmulator skipped:', error?.message || error)
    }
  }
}

// Initialize storage with custom settings
const storage = getStorage(app)

// Add global error handler for storage operations (only in browser)
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('unhandledrejection', function (event) {
    if (event.reason && event.reason.code === 'storage/unauthorized') {
      console.error('Storage error:', event.reason)
      // Handle unauthorized storage access
    }
  })
}

// Network state management (only in browser, production Firestore only)
// In emulator mode, browser "offline" events can be true even when localhost
// emulators are reachable; forcing disableNetwork() would incorrectly strand
// the SDK in offline mode.
let isNetworkEnabled = true

const shouldManageNetworkState = !useFirebaseEmulators

// Network state change handler
const handleNetworkStateChange = async () => {
  if (!shouldManageNetworkState) return

  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true

  if (isOnline && !isNetworkEnabled) {
    try {
      await new Promise((resolve) => setTimeout(resolve, 2000))
      await enableNetwork(db)
      isNetworkEnabled = true
    } catch (error) {
      console.error('Error enabling network:', error)
    }
  } else if (!isOnline && isNetworkEnabled) {
    try {
      await disableNetwork(db)
      isNetworkEnabled = false
    } catch (error) {
      console.error('Error disabling network:', error)
    }
  }
}

// Initialize network state monitoring (browser-only)
if (shouldManageNetworkState && typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('online', handleNetworkStateChange)
  window.addEventListener('offline', handleNetworkStateChange)
}

// Initialize analytics
let analytics = null
;(async () => {
  try {
    // Only initialize analytics in supported (browser) contexts
    const supported = typeof window !== 'undefined' && (await analyticsIsSupported())
    if (supported) {
      analytics = getAnalytics(app)
    }
  } catch (error) {
    console.warn('Analytics initialization failed:', error)
  }
})()

// Ensure users collection exists
const ensureUsersCollection = async () => {
  // Only proceed if a user is logged in
  if (!auth.currentUser) {
    return
  }

  let retries = 0
  const maxRetries = 3

  while (retries < maxRetries) {
    try {
      const usersCollectionRef = collection(db, 'users')
      const snapshot = await getDocs(usersCollectionRef)

      if (snapshot.empty) {
        const adminUser = {
          uid: 'admin-placeholder',
          email: 'admin@example.com',
          displayName: 'Admin Placeholder',
          createdAt: new Date().toISOString(),
          roles: {
            admin: true,
            player: true
          },
          isPlaceholder: true
        }

        await setDoc(doc(db, 'users', 'admin-placeholder'), adminUser)
      }
      break
    } catch (error) {
      retries++
      if (retries === maxRetries) {
        console.error('Failed to ensure users collection after maximum retries:', error)
      } else {
        await new Promise((resolve) => setTimeout(resolve, 2000 * retries))
      }
    }
  }
}

// Instead of running on a timer, listen for auth state changes
// NOTE: We intentionally do NOT call ensureUsersCollection here to avoid
// running Firestore queries concurrently during auth initialization. The
// app's boot code will call `ensureUsersCollection()` after it has finished
// setting up the user state. This prevents IndexedDB version-change/upgrade
// races that can abort transactions when multiple listeners run at the same time.

export { app, db, auth, storage, analytics, functions, httpsCallable, ensureUsersCollection }
